// src/components/surveys/builder/QuestionEditor.tsx
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  X,
  Plus,
  Trash2,
  GripVertical,
  Settings,
  AlertCircle,
} from 'lucide-react';
import { SurveyQuestion, QuestionOption, QuestionType } from '../../../types/survey.types';

interface QuestionEditorProps {
  question: SurveyQuestion;
  onUpdate: (updates: Partial<SurveyQuestion>) => void;
  onClose: () => void;
  validationErrors?: string[];
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  onUpdate,
  onClose,
  validationErrors = [],
}) => {
  // Handle adding a new option
  const handleAddOption = () => {
    const newOption: QuestionOption = {
      id: uuidv4(),
      text: `Option ${(question.options?.length || 0) + 1}`,
      order: question.options?.length || 0,
    };
    onUpdate({
      options: [...(question.options || []), newOption],
    });
  };

  // Handle updating an option
  const handleUpdateOption = (optionId: string, text: string) => {
    const updatedOptions = question.options?.map((opt) =>
      opt.id === optionId ? { ...opt, text } : opt
    );
    onUpdate({ options: updatedOptions });
  };

  // Handle deleting an option
  const handleDeleteOption = (optionId: string) => {
    const updatedOptions = question.options
      ?.filter((opt) => opt.id !== optionId)
      .map((opt, index) => ({ ...opt, order: index }));
    onUpdate({ options: updatedOptions });
  };

  // Check if question type has options
  const hasOptions = ['multiple_choice', 'multi_select', 'dropdown'].includes(question.type);

  // Check if question type has rating config
  const hasRatingConfig = question.type === 'rating_scale';

  return (
    <div className="bg-white border-l border-gray-200 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
        <h3 className="font-semibold text-gray-900">Edit Question</h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="p-4 space-y-6">
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
              <AlertCircle className="h-4 w-4" />
              Validation Errors
            </div>
            {validationErrors.map((error, index) => (
              <p key={index} className="text-sm text-red-600">
                {error}
              </p>
            ))}
          </div>
        )}

        {/* Question Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Question Text *
          </label>
          <input
            type="text"
            value={question.text}
            onChange={(e) => onUpdate({ text: e.target.value })}
            placeholder="Enter your question..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (optional)
          </label>
          <textarea
            value={question.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="Add a description or help text..."
            rows={2}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Required Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Required
            </label>
            <p className="text-xs text-gray-500">
              Respondent must answer this question
            </p>
          </div>
          <button
            onClick={() => onUpdate({ required: !question.required })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              question.required ? 'bg-indigo-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                question.required ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Lead capture gate toggle — only on yes_no questions */}
        {question.type === 'yes_no' && (
          <div className="flex items-center justify-between py-3 border-t border-gray-100">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                If Yes → Open Lead Form
              </label>
              <p className="text-xs text-gray-500">
                When the canvasser answers "Yes", the survey exits and opens the full lead capture form pre-filled with the current address.
              </p>
            </div>
            <button
              onClick={() => onUpdate({ triggerLeadCapture: !question.triggerLeadCapture })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                question.triggerLeadCapture ? 'bg-emerald-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  question.triggerLeadCapture ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}

        {/* Options Editor (for choice questions) */}
        {hasOptions && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            <div className="space-y-2">
              {question.options?.map((option, index) => (
                <div key={option.id} className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => handleUpdateOption(option.id, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    onClick={() => handleDeleteOption(option.id)}
                    disabled={(question.options?.length || 0) <= 2}
                    className={`p-1.5 rounded ${
                      (question.options?.length || 0) <= 2
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddOption}
                className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 mt-2"
              >
                <Plus className="h-4 w-4" />
                Add Option
              </button>
            </div>
          </div>
        )}

        {/* Rating Scale Config */}
        {hasRatingConfig && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Rating Scale Configuration
            </label>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Value</label>
                <input
                  type="number"
                  value={question.ratingConfig?.min || 1}
                  onChange={(e) =>
                    onUpdate({
                      ratingConfig: {
                        ...question.ratingConfig,
                        min: parseInt(e.target.value) || 1,
                        max: question.ratingConfig?.max || 5,
                      },
                    })
                  }
                  min={0}
                  max={10}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Value</label>
                <input
                  type="number"
                  value={question.ratingConfig?.max || 5}
                  onChange={(e) =>
                    onUpdate({
                      ratingConfig: {
                        ...question.ratingConfig,
                        min: question.ratingConfig?.min || 1,
                        max: parseInt(e.target.value) || 5,
                      },
                    })
                  }
                  min={1}
                  max={10}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Label</label>
                <input
                  type="text"
                  value={question.ratingConfig?.minLabel || ''}
                  onChange={(e) =>
                    onUpdate({
                      ratingConfig: {
                        ...question.ratingConfig,
                        min: question.ratingConfig?.min || 1,
                        max: question.ratingConfig?.max || 5,
                        minLabel: e.target.value,
                      },
                    })
                  }
                  placeholder="e.g., Poor"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Label</label>
                <input
                  type="text"
                  value={question.ratingConfig?.maxLabel || ''}
                  onChange={(e) =>
                    onUpdate({
                      ratingConfig: {
                        ...question.ratingConfig,
                        min: question.ratingConfig?.min || 1,
                        max: question.ratingConfig?.max || 5,
                        maxLabel: e.target.value,
                      },
                    })
                  }
                  placeholder="e.g., Excellent"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Style</label>
              <select
                value={question.ratingConfig?.style || 'numbers'}
                onChange={(e) =>
                  onUpdate({
                    ratingConfig: {
                      ...question.ratingConfig,
                      min: question.ratingConfig?.min || 1,
                      max: question.ratingConfig?.max || 5,
                      style: e.target.value as 'numbers' | 'stars' | 'emoji',
                    },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
              >
                <option value="numbers">Numbers</option>
                <option value="stars">Stars</option>
                <option value="emoji">Emoji</option>
              </select>
            </div>
          </div>
        )}

        {/* Text Validation */}
        {question.type === 'text' && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Text Validation
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Length</label>
                <input
                  type="number"
                  value={question.validation?.minLength || ''}
                  onChange={(e) =>
                    onUpdate({
                      validation: {
                        ...question.validation,
                        minLength: e.target.value ? parseInt(e.target.value) : undefined,
                      },
                    })
                  }
                  min={0}
                  placeholder="0"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Length</label>
                <input
                  type="number"
                  value={question.validation?.maxLength || 500}
                  onChange={(e) =>
                    onUpdate({
                      validation: {
                        ...question.validation,
                        maxLength: parseInt(e.target.value) || 500,
                      },
                    })
                  }
                  min={1}
                  placeholder="500"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Number Validation */}
        {question.type === 'number' && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Number Validation
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Value</label>
                <input
                  type="number"
                  value={question.validation?.min ?? ''}
                  onChange={(e) =>
                    onUpdate({
                      validation: {
                        ...question.validation,
                        min: e.target.value ? parseFloat(e.target.value) : undefined,
                      },
                    })
                  }
                  placeholder="No minimum"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Value</label>
                <input
                  type="number"
                  value={question.validation?.max ?? ''}
                  onChange={(e) =>
                    onUpdate({
                      validation: {
                        ...question.validation,
                        max: e.target.value ? parseFloat(e.target.value) : undefined,
                      },
                    })
                  }
                  placeholder="No maximum"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Step</label>
              <input
                type="number"
                value={question.validation?.step || 1}
                onChange={(e) =>
                  onUpdate({
                    validation: {
                      ...question.validation,
                      step: parseFloat(e.target.value) || 1,
                    },
                  })
                }
                min={0.01}
                step={0.01}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}

        {/* Date Validation */}
        {question.type === 'date' && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Date Validation
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Date</label>
                <input
                  type="date"
                  value={question.validation?.minDate || ''}
                  onChange={(e) =>
                    onUpdate({
                      validation: {
                        ...question.validation,
                        minDate: e.target.value || undefined,
                      },
                    })
                  }
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Date</label>
                <input
                  type="date"
                  value={question.validation?.maxDate || ''}
                  onChange={(e) =>
                    onUpdate({
                      validation: {
                        ...question.validation,
                        maxDate: e.target.value || undefined,
                      },
                    })
                  }
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Advanced Settings */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
            <Settings className="h-4 w-4" />
            Advanced Settings
          </div>

          {/* Default Visible */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm text-gray-700">
                Default Visible
              </label>
              <p className="text-xs text-gray-500">
                Show this question by default
              </p>
            </div>
            <button
              onClick={() => onUpdate({ defaultVisible: !question.defaultVisible })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                question.defaultVisible !== false ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  question.defaultVisible !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionEditor;
